import { Reducer } from 'redux';
import { flow, indexOf, isUndefined, set } from 'lodash/fp';

import { NotificationState, NotificationActionTypes } from './types';
import { LoginActionTypes } from '../../login/store/types';

export const INITIAL_STATE: NotificationState = {
    notifications: [],
    modalOpen: false,
    systemNotification: null,
    activeMLDocuments: [],
    activeMLOpinions: [],
    activeFewShotDocuments: [],
    mlProgressModalOpen: false,
    portfolioStatistics: [],
    showPortfolioStatistics: true,
    testMLDocuments: []
};

export const notificationReducer: Reducer<NotificationState> = (state = INITIAL_STATE, { type, payload }): NotificationState => {
    switch (type) {
        case NotificationActionTypes.NOTIFICATION_RECEIVED:
            return set('notifications', payload, state);
        case NotificationActionTypes.TOGGLE_NOTIFICATION_MODAL_OPEN:
            return set('modalOpen', payload, state);
        case NotificationActionTypes.TOGGLE_SYSTEM_NOTIFICATION_MODAL:
            return set('systemNotification', payload, state);
        case NotificationActionTypes.TOGGLE_ML_PROGRESS_MODAL_OPEN:
            return set('mlProgressModalOpen', payload, state);
        case NotificationActionTypes.SET_ACTIVE_ML_DOCUMENTS:
            return set('activeMLDocuments', payload, state);
        case NotificationActionTypes.REMOVE_ACTIVE_ML_DOCUMENT: {
            const activeMLDocuments = state.activeMLDocuments.filter(({ documentId }) => documentId !== payload);
            const noActiveDocuments = [activeMLDocuments, state.activeMLOpinions, state.activeFewShotDocuments, state.testMLDocuments].every(activeDocuments => activeDocuments.length === 0);
            if (noActiveDocuments) {
                return flow(
                    set('mlProgressModalOpen', false),
                    set('activeMLDocuments', activeMLDocuments)
                )(state);
            }
            return set('activeMLDocuments', activeMLDocuments, state);
        }
        case NotificationActionTypes.UPDATE_PERCENTAGE_ACTIVE_ML_DOCUMENT: {
            const includedInActiveDocuments = !isUndefined(state.activeMLDocuments.find(({ documentId }) => documentId === payload.documentId));
            if (includedInActiveDocuments) {
                const documentIds = state.activeMLDocuments.map(({ documentId }) => documentId);
                const index = indexOf(payload.documentId, documentIds);
                return set(`activeMLDocuments[${index}]`, payload, state);
            } else {
                const activeMLDocuments = [...state.activeMLDocuments, payload];
                return set('activeMLDocuments', activeMLDocuments, state);
            }
        }
        case NotificationActionTypes.SET_ACTIVE_ML_OPINIONS:
            return set('activeMLOpinions', payload, state);
        case NotificationActionTypes.REMOVE_ACTIVE_ML_OPINION: {
            const activeMLOpinions = state.activeMLOpinions.filter(({ opinionId }) => opinionId !== payload);
            const noActiveDocuments = [activeMLOpinions, state.activeMLDocuments, state.activeFewShotDocuments, state.testMLDocuments].every(activeDocuments => activeDocuments.length === 0);
            if (noActiveDocuments) {
                return flow(
                    set('mlProgressModalOpen', false),
                    set('activeMLOpinions', activeMLOpinions)
                )(state);
            }
            return set('activeMLOpinions', activeMLOpinions, state);
        }
        case NotificationActionTypes.UPDATE_PERCENTAGE_ACTIVE_ML_OPINION: {
            const includedInActiveDocuments = !isUndefined(state.activeMLOpinions.find(({ opinionId }) => opinionId === payload.opinionId));
            if (includedInActiveDocuments) {
                const opinionIds = state.activeMLOpinions.map(({ opinionId }) => opinionId);
                const index = indexOf(payload.opinionId, opinionIds);
                return set(`activeMLOpinions[${index}]`, payload, state);
            } else {
                const activeMLOpinions = [...state.activeMLOpinions, payload];
                return set('activeMLOpinions', activeMLOpinions, state);
            }
        }
        case NotificationActionTypes.SET_ACTIVE_FEW_SHOT_DOCUMENTS:
            return set('activeFewShotDocuments', payload, state);
        case NotificationActionTypes.REMOVE_ACTIVE_FEW_SHOT_DOCUMENT: {
            const activeFewShotDocuments = state.activeFewShotDocuments.filter(({ mlFewShotDocumentId }) => mlFewShotDocumentId !== payload);
            const noActiveDocuments = [activeFewShotDocuments, state.activeMLDocuments, state.activeMLOpinions, state.testMLDocuments].every(activeDocuments => activeDocuments.length === 0);
            if (noActiveDocuments) {
                return flow(
                    set('mlProgressModalOpen', false),
                    set('activeFewShotDocuments', activeFewShotDocuments)
                )(state);
            }
            return set('activeFewShotDocuments', activeFewShotDocuments, state);
        }
        case NotificationActionTypes.UPDATE_PERCENTAGE_ACTIVE_FEW_SHOT_DOCUMENT: {
            const includedInActiveDocuments = !isUndefined(state.activeFewShotDocuments.find(({ mlFewShotDocumentId }) => mlFewShotDocumentId === payload.mlFewShotDocumentId));
            if (includedInActiveDocuments) {
                const mlFewShotDocumentIds = state.activeFewShotDocuments.map(({ mlFewShotDocumentId }) => mlFewShotDocumentId);
                const index = indexOf(payload.mlFewShotDocumentId, mlFewShotDocumentIds);
                return set(`activeFewShotDocuments[${index}]`, payload, state);
            } else {
                const activeFewShotDocuments = [...state.activeFewShotDocuments, payload];
                return set('activeFewShotDocuments', activeFewShotDocuments, state);
            }
        }
        case NotificationActionTypes.SET_PORTFOLIO_STATISTICS:
            return flow(
                set('portfolioStatistics', payload),
                set('showPortfolioStatistics', true)
            )(state);
        case NotificationActionTypes.TOGGLE_SHOW_PORTFOLIO_STATISTICS:
            return set('showPortfolioStatistics', payload, state);
        case NotificationActionTypes.UPDATE_PERCENTAGE_TEST_ML_DOCUMENT: {
            const includedInTestMLDocuments = !isUndefined(state.testMLDocuments.find(({ documentId }) => documentId === payload.documentId));
            if (includedInTestMLDocuments) {
                const documentIds = state.testMLDocuments.map(({ documentId }) => documentId);
                const index = indexOf(payload.documentId, documentIds);
                return set(`testMLDocuments[${index}]`, payload, state);
            } else {
                const testMLDocuments = [...state.testMLDocuments, payload];
                return set('testMLDocuments', testMLDocuments, state);
            }
        }
        case NotificationActionTypes.REMOVE_TEST_ML_DOCUMENT: {
            const testMLDocuments = state.testMLDocuments.filter(({ documentId }) => documentId !== payload);
            const noActiveDocuments = [testMLDocuments, state.activeMLDocuments, state.activeMLOpinions, state.activeFewShotDocuments].every(activeDocuments => activeDocuments.length === 0);
            if (noActiveDocuments) {
                return flow(
                    set('mlProgressModalOpen', false),
                    set('testMLDocuments', testMLDocuments)
                )(state);
            }
            return set('testMLDocuments', testMLDocuments, state);
        }
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
